import type { ReactNode } from "react";
import { MangoMark } from "../components/shared/MangoMark";

/* ────────────────────────────────────────────────────────────────────────────
   Terms of Service + Privacy Policy — public, app-served (app.grafty.io/terms,
   /privacy). Content mirrors the platform marketing site (grafty-sites) so the
   signup checkboxes link to something real while grafty.io (the apex) isn't live.
   Theme-aware (CSS vars) so they read correctly in light + dark mode.
──────────────────────────────────────────────────────────────────────────── */

const EFFECTIVE = "June 13, 2026";

function Section({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section style={{ marginTop: 32 }}>
      <h2 style={{
        fontFamily: "var(--font-display)", fontSize: "1.2rem", fontWeight: 700,
        letterSpacing: "-0.01em", color: "var(--color-text-primary)", marginBottom: 10,
      }}>{heading}</h2>
      <div style={{ color: "var(--color-text-secondary)", fontSize: 15, lineHeight: 1.7, display: "grid", gap: 12 }}>
        {children}
      </div>
    </section>
  );
}

function LegalShell({ title, intro, children }: { title: string; intro: ReactNode; children: ReactNode }) {
  return (
    <div style={{
      minHeight: "100dvh", background: "var(--color-bg-base)",
      fontFamily: "var(--font-body)", padding: "40px 16px",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <MangoMark size={40} />
            <span style={{
              fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700,
              color: "var(--brand-mango)", letterSpacing: "-0.03em",
            }}>Grafty</span>
          </a>
        </div>

        <div style={{
          background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
          borderRadius: 20, padding: "40px 40px",
        }}>
          <h1 style={{
            fontFamily: "var(--font-display)", fontSize: "2rem", fontWeight: 700,
            letterSpacing: "-0.02em", color: "var(--color-text-primary)", marginBottom: 6,
          }}>{title}</h1>
          <p style={{ fontSize: 13, color: "var(--color-text-tertiary)", marginBottom: 20 }}>
            Effective {EFFECTIVE}
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--color-text-secondary)" }}>{intro}</p>
          {children}
        </div>

        <p style={{ textAlign: "center", fontSize: 13, color: "var(--color-text-tertiary)", marginTop: 24 }}>
          <a href="/terms" style={{ color: "var(--color-text-tertiary)" }}>Terms</a>
          {" · "}
          <a href="/privacy" style={{ color: "var(--color-text-tertiary)" }}>Privacy</a>
          {" · "}
          <a href="mailto:hello@grafty.io" style={{ color: "var(--brand-mango)" }}>hello@grafty.io</a>
        </p>
      </div>
    </div>
  );
}

const mango: React.CSSProperties = { color: "var(--brand-mango)" };

export function TermsPage() {
  return (
    <LegalShell
      title="Terms of Service"
      intro="These terms govern your access to and use of Grafty. By creating an account or using the service, you agree to them."
    >
      <Section heading="Your account">
        <p>You're responsible for your account, keeping your credentials secure, and the activity that happens under it. You must provide accurate information and be authorized to act for any farm you manage in Grafty.</p>
      </Section>
      <Section heading="Acceptable use">
        <p>Don't misuse the service: no unlawful activity, no attempts to breach security or access other tenants' data, no reverse engineering, and no automated abuse of our APIs or automated features beyond documented limits. We may suspend accounts that put the platform or other customers at risk.</p>
      </Section>
      <Section heading="Your data">
        <p>You own the farm data you put into Grafty. You grant us the limited rights needed to host, process, and display it to operate the service (including running the features you enable). You can export or delete your data as described in our{" "}
          <a href="/privacy" style={mango}>Privacy Policy</a>.</p>
      </Section>
      <Section heading="Plans & billing">
        <p>Paid plans renew automatically until cancelled; cancelling stops future charges and you keep access through the end of the paid period. Prices and plan limits are shown at sign-up and in your billing settings. Payments are handled by Stripe.</p>
      </Section>
      <Section heading="Automated features & advisory content">
        <p>Automated outputs (diagnoses, pricing estimates, recommendations) and reference content are provided for informational purposes only and may be inaccurate. They are not professional agronomic, veterinary, medical, financial, legal, or tax advice. Use your own judgment and consult a qualified professional where appropriate.</p>
      </Section>
      <Section heading="Disclaimers & liability">
        <p>The service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, Grafty is not liable for indirect, incidental, or consequential damages, and our total liability is limited to the amounts you paid us in the 12 months before the claim.</p>
      </Section>
      <Section heading="Changes & contact">
        <p>We may update these terms; material changes will be notified and the effective date revised. Continued use after changes means you accept them. Questions:{" "}
          <a href="mailto:hello@grafty.io" style={mango}>hello@grafty.io</a>.</p>
      </Section>
    </LegalShell>
  );
}

export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      intro="This policy explains what personal information Grafty collects, how we use and share it, and the choices and rights you have. It applies to grafty.io, the Grafty app, and tenant microsites we host."
    >
      <Section heading="Information we collect">
        <p>We collect: (a) <strong>account information</strong> you provide (name, email, farm details, login credentials, or a Google/Apple ID if you use social sign-in); (b) <strong>farm and operational data</strong> you enter (plants, harvests, care logs, expenses, photos, etc.); (c) <strong>usage and device data</strong> (pages viewed, approximate location from IP, browser type); and (d) <strong>payment information</strong>, which is processed by Stripe — we never see or store full card numbers.</p>
      </Section>
      <Section heading="How we use information">
        <p>To provide and operate the service, authenticate you, process payments and subscriptions, send transactional email (e.g. receipts, invites, password resets), provide support, improve the product, and meet legal obligations. Automated features only process the specific data you submit to them, and only when you have enabled them.</p>
      </Section>
      <Section heading="How we share information — and what we don't">
        <p><strong>We do not sell your personal information, and we do not share it for cross-context behavioral advertising.</strong> We share data only with service providers that help us run Grafty, under contract and only as needed:</p>
        <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6, fontSize: 14 }}>
          <li>Stripe — payment processing</li>
          <li>Loops.so — transactional email delivery</li>
          <li>Google & Apple — only if you choose social sign-in (OAuth)</li>
          <li>Google Cloud Platform — hosting, storage, and automated features (Gemini)</li>
          <li>Product analytics — only if you consent to analytics cookies</li>
        </ul>
        <p>We may also disclose information if required by law or to protect rights and safety.</p>
      </Section>
      <Section heading="Your privacy rights">
        <p>Depending on where you live (including California's CCPA/CPRA and other U.S. state laws, and the EU/UK GDPR), you may have the right to access, correct, delete, or obtain a portable copy of your personal information, and to opt out of sale/sharing (we do neither). You can exercise these rights from inside the app (Settings → Privacy) or by emailing{" "}
          <a href="mailto:privacy@grafty.io" style={mango}>privacy@grafty.io</a>. We respond within the timeframe the applicable law requires (typically 45 days). We will not discriminate against you for exercising a right.</p>
        <p>For farm accounts, the farm (our customer) is the controller of its users' data and Grafty acts as its processor; direct individual requests to your farm administrator or to us and we will route them appropriately.</p>
      </Section>
      <Section heading="Data retention & security">
        <p>We keep personal information for as long as your account is active and as needed for the purposes above or to meet legal requirements, then delete or anonymize it. We protect data with encryption in transit, tenant isolation, access controls, and audit logging. No system is perfectly secure, but we work to safeguard your information.</p>
      </Section>
      <Section heading="Children">
        <p>Grafty is not directed to children and is not intended for anyone under 16. We do not knowingly collect personal information from children.</p>
      </Section>
      <Section heading="Changes & contact">
        <p>We may update this policy; we'll revise the effective date above and, for material changes, provide notice. Questions or requests:{" "}
          <a href="mailto:privacy@grafty.io" style={mango}>privacy@grafty.io</a>.</p>
      </Section>
    </LegalShell>
  );
}
